import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { Briefcase, MapPin, Calendar, Building, Search } from 'lucide-react';

const MyJobs = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (userProfile?.id) {
      fetchApplications();
    }
  }, [userProfile]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${userProfile.id}/applications`
      );
      if (response.data.success) {
        setApplications(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = filter === 'all'
    ? applications
    : applications.filter(a => a.status === filter);

  const statusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Applied';
      case 'reviewing': return 'Under Review';
      case 'shortlisted': return 'Shortlisted';
      case 'rejected': return 'Not Selected';
      case 'hired': return 'Hired';
      default: return status || 'Applied';
    }
  };

  const statusStyle = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'reviewing': return 'bg-blue-100 text-blue-700';
      case 'shortlisted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'hired': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const matchScoreStyle = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-700';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Applied' },
    { key: 'reviewing', label: 'Under Review' },
    { key: 'shortlisted', label: 'Shortlisted' },
    { key: 'rejected', label: 'Not Selected' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-jobs-page">
      <ResponsiveNavigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="my-jobs-heading">My Jobs</h1>
          <p className="text-sm text-gray-500 mt-1">
            {applications.length} application{applications.length !== 1 ? 's' : ''} total
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="my-jobs-filters">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Applications List */}
        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading applications...</p>
        ) : filteredApps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {filter === 'all' ? 'No applications yet' : `No ${filters.find(f => f.key === filter)?.label?.toLowerCase()} applications`}
            </p>
            <button
              onClick={() => navigate('/jobs')}
              className="mt-3 text-blue-600 hover:underline text-sm font-medium"
            >
              Browse open positions
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="applications-list">
            {filteredApps.map((app) => (
              <div
                key={app.id}
                onClick={() => navigate(`/jobs/${app.position_id}`)}
                className="bg-white rounded-lg border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                data-testid={`application-card-${app.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-blue-600 mb-1">
                      {app.positions?.title || 'Position'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5" />
                      {app.positions?.company_name || app.positions?.organizations?.name || 'Company'}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      {app.positions?.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {app.positions.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Applied {new Date(app.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 ml-4 flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle(app.status)}`}>
                      {statusLabel(app.status)}
                    </span>
                    {app.match_score !== null && app.match_score !== undefined && (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${matchScoreStyle(app.match_score)}`} data-testid="match-score-badge">
                        {app.match_score}% Match
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyJobs;
